import { useState } from 'react'
import { Search } from 'lucide-react'
import { SearchInput } from '@/components/search-input'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { LoadingButton } from '@/components/loading-button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatPhone } from '@/lib/phone'
import { formatMessageTime } from '@/features/communications/message-format'
import { CallBody } from '@/features/phone-calls/call-details'
import { CallIcon } from '@/features/phone-calls/calls-panel'
import { callLine } from '@/features/phone-calls/call-format'
import { useLinkCall, useRecentCalls, type CallTarget } from '@/features/phone-calls/hooks'

/**
 * Record Communication's recent Quo calls (USA-217): pick one to preview it, then link it to this
 * conversation's record, for a call the automatic filing put somewhere else or nowhere.
 */
export function RecentCallPicker({
  target,
  onLinked,
  onPickedChange,
}: {
  target: CallTarget
  onLinked: () => void
  /** Whether a call is picked: the typed note and its Record button step aside meanwhile. */
  onPickedChange?: (picked: boolean) => void
}) {
  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search, 250)
  const calls = useRecentCalls(target, true, debounced)
  const link = useLinkCall()
  const [picked, setPickedState] = useState<string | null>(null)
  const setPicked = (uuid: string | null) => {
    setPickedState(uuid)
    onPickedChange?.(uuid !== null)
  }

  const found = calls.data ?? []
  const chosen = found.find((call) => call.uuid === picked)

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search calls: name, number, words said"
          aria-label="Search calls"
          className="h-8 pl-8 text-sm"
        />
      </div>
      {calls.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : found.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">{debounced.trim() ? 'No calls match' : 'No recent calls'}</p>
      ) : (
      <ul className="max-h-72 divide-y overflow-y-auto rounded-md border bg-background" role="listbox" aria-label="Recent calls">
        {found.map((call) => {
          const selected = call.uuid === picked
          const who = call.party_name || (call.their_number ? formatPhone(call.their_number) : null)
          return (
            <li key={call.uuid}>
              <button
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => setPicked(selected ? null : call.uuid)}
                className={cn(
                  'flex w-full items-start gap-2.5 px-2.5 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
                  selected && 'bg-sky-50 dark:bg-sky-950/40',
                )}
              >
                <CallIcon call={call} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium">{who ?? callLine(call)}</span>
                    <time dateTime={call.started_at} className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatMessageTime(call.started_at)}
                    </time>
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {callLine(call)}
                    {call.filed_on ? ` · on ${call.filed_on.label}` : ''}
                  </span>
                  {call.summary?.summary[0] && (
                    <span className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{call.summary.summary[0]}</span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      )}
      {chosen && (
        <div className="space-y-3 pt-1">
          <CallBody call={chosen} />
          <LoadingButton
            size="sm"
            loading={link.isPending}
            loadingText="Linking..."
            onClick={() => link.mutate({ callUuid: chosen.uuid, target }, { onSuccess: onLinked })}
          >
            Link call
          </LoadingButton>
        </div>
      )}
    </div>
  )
}
